import { AgxDiagonalUpArrowIcon, AgxCircleIcon } from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useAgentsInAgency, useSelectedCampaign, useSelectedContract } from 'hooks';
import { cleanTwoLineAddress } from 'helpers/address';
import CampaignImage from './components/CampaignImage';
import ContractSectionHeader from './ContractSectionHeader';
import './CampaignSectionHeader.scss';
import { useMemo } from 'react';
import {
  AgxAvatarLabelGroup,
  AgxBadge,
  BadgeColor,
  AgxTypography,
  AgxSpacer,
} from '@urbanx/agx.ui-components2';
import { MenuTitles, Pages, SELECTED_CAMPAIGN_TOP_MENU } from 'constants/menu';
import { SideMenu } from 'types/commonTypes';
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles';
import { Link, useLocation, useParams } from 'react-router-dom';
import slugify from 'slugify';
import { LaunchDetails, LaunchDetailsStatus } from '@urbanx/agx.campaigns.client';
import { useGetUrbanXTeamForAgency } from '@urbanx/agx.agencies.client';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';

dayjs.extend(advancedFormat);

const CampaignHeader = () => {
  const { agencyId, campaignId } = useParams();
  const location = useLocation();
  const { campaign: selectedCampaign } = useSelectedCampaign();
  const { contract: selectedContract } = useSelectedContract();
  const { data: agents } = useAgentsInAgency();
  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles();
  const { data: urbanXTeamForAgency } = useGetUrbanXTeamForAgency(agencyId ?? '', !!agencyId);
  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    selectedCampaign?.campaignId ?? '',
    !!selectedCampaign?.campaignId
  );

  const launchDateDetails = useMemo<LaunchDetails | undefined>(() => {
    const details = selectedCampaign?.launchDateDetails;
    if (!details?.status) return undefined;

    return {
      launchDate: details.launchDate,
      status: details.status as unknown as LaunchDetailsStatus,
    };
  }, [selectedCampaign?.launchDateDetails]);

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(selectedCampaign?.address);

  const activeTopMenuFromPath = (() => {
    if (!campaignId || !location.pathname) return undefined;
    const campaignPath = `/campaigns/${campaignId}/`;
    const afterCampaign = location.pathname.split(campaignPath)[1] ?? '';
    const firstSegment = afterCampaign.split('/')[0] ?? '';
    if (firstSegment === Pages.FINANCE || afterCampaign.startsWith(`${Pages.FINANCE}/`)) {
      return MenuTitles.FINANCE;
    }
    if (firstSegment === Pages.CONTRACTS || afterCampaign.startsWith(`${Pages.CONTRACTS}/`)) {
      return MenuTitles.CONTRACTS;
    }
    if (firstSegment === Pages.ACTIVITY) return MenuTitles.ACTIVITY;
    if (firstSegment === Pages.FILES) return MenuTitles.FILES;
    if (
      firstSegment === Pages.PROPERTY_INFORMATION ||
      firstSegment === Pages.PROPERTY_INFORMATION_SECTION ||
      firstSegment === Pages.CAMPAIGN_OVERVIEW ||
      firstSegment === Pages.CAMPAIGN_TASKS ||
      firstSegment === Pages.CAMPAIGN_MARKETING ||
      firstSegment === Pages.PERMISSIONS
    ) {
      return MenuTitles.CAMPAIGN;
    }
    return undefined;
  })();

  const generateLink = (pageLink: string) => {
    if (pageLink === '') return '/';

    if (agencyId) {
      return `/${Pages.AGENCIES}/${agencyId}/campaigns/${campaignId}/${pageLink}`;
    }
    return pageLink || '#';
  };

  const renderTopMenu = (menuItems: SideMenu[], activeMenu?: MenuTitles) => {
    return menuItems
      .filter((menu) => !menu.isHidden)
      .filter((menu) => doesUserHaveRequiredRoles(menu.requiredRoles))
      .filter((menu) => menu.includeOnlyInState.length === 0)
      .map((menu, index) => {
        const menuLink = generateLink(menu.pageLink ?? '');

        return (
          <li
            key={`sideMenu_${index}`}
            className={`secondaryMenuItemTopMenu ${
              activeMenu && activeMenu === menu.title ? 'active' : ''
            }`}
          >
            <Link
              data-testid={`link-${slugify(menu.title)}`}
              to={menuLink}
              state={{ activeTab: menu.title }}
              replace={location.pathname === menuLink}
            >
              {menu.title}
            </Link>
          </li>
        );
      });
  };
  const generateListingAgents = () => {
    const listingAgents = [];

    if (selectedCampaign?.leadAgentId) {
      const leadAgent = agents?.find((agent) => agent.id === selectedCampaign.leadAgentId);

      if (leadAgent) {
        listingAgents.push(
          <AgxAvatarLabelGroup
            id={`avatar-${leadAgent.name?.firstName}`}
            key={leadAgent.id}
            type="avatar"
            variant="round"
            photoUrl={leadAgent.photoUrl}
            label1={`${leadAgent.name?.firstName} ${leadAgent.name?.lastName}`}
            size="small"
          />
        );
      }
    }

    if (selectedCampaign?.secondAgentId) {
      const secondAgent = agents?.find((agent) => agent.id === selectedCampaign.secondAgentId);

      if (secondAgent && secondAgent?.softDeleted === false) {
        listingAgents.push(
          <AgxAvatarLabelGroup
            id={`avatar-${secondAgent.name?.firstName}`}
            key={secondAgent.id}
            type="avatar"
            variant="round"
            photoUrl={secondAgent.photoUrl}
            label1={`${secondAgent.name?.firstName} ${secondAgent.name?.lastName}`}
            size="small"
          />
        );
      }
    }

    if (urbanXTeamForAgency?.adminManager) {
      const adminManager = urbanXTeamForAgency?.adminManager;
      listingAgents.push(
        <AgxSpacer
          key={`adminManager-${adminManager.email}`}
          direction="horizontal"
          gap={'3xs'}
          contentAlignment="center"
        >
          <AgxAvatarLabelGroup
            id={`avatar-${adminManager.firstName}`}
            type="avatar"
            variant="round"
            photoUrl={adminManager.photo?.publicPath}
            label1={`${adminManager.firstName} ${adminManager.lastName}`}
            size="small"
          />
          <AgxTypography
            id={`lblAdminManager`}
            type={'title'}
            size={'small'}
            color="var(--text-tertiary)"
          >
            {`(Admin manager)`}
          </AgxTypography>
        </AgxSpacer>
      );
    }

    return (
      <AgxSpacer className="campaignHeaderAgentsRow" direction="horizontal" gap={'sm'}>
        {listingAgents}
      </AgxSpacer>
    );
  };

  const generateListingBadges = () => {
    const listingBadges = [];
    const rexListingLink =
      rexListing?.listingId && rexListing?.brandAccountId
        ? `https://app.rexsoftware.com/listings/#id=${encodeURIComponent(
            rexListing.listingId
          )}&_account_id=${encodeURIComponent(rexListing.brandAccountId)}`
        : undefined;

    listingBadges.push(
      <AgxBadge
        key="ListingBadgeBlankContractIsLive"
        type="colourfill"
        size="md"
        colour={selectedCampaign?.blankContractIsLive ? 'green' : 'red'}
        text={selectedCampaign?.blankContractIsLive ? 'Contract Live' : 'Contract Offline'}
      />
    );

    if (launchDateDetails) {
      if (
        launchDateDetails.status === LaunchDetailsStatus.Confirmed &&
        launchDateDetails.launchDate
      ) {
        const date = dayjs(launchDateDetails.launchDate);
        const today = dayjs();
        let status: 'Today' | 'Overdue' | 'Future' = 'Future';
        if (date.isSame(today, 'day')) {
          status = 'Today';
        } else if (date.isBefore(today, 'day')) {
          status = 'Overdue';
        }
        const colour = (
          status === 'Overdue' ? 'red' : status === 'Today' ? 'yellow' : 'orange'
        ) as BadgeColor;
        const text = `Launch ${date.format('Do MMM')}`;

        if (status === 'Today' || status === 'Overdue') {
          listingBadges.push(
            <AgxBadge
              key="ListingBadgeLaunchDate"
              type="highlighted"
              colour={colour}
              size="md"
              text={text}
              highlightText={status}
            />
          );
        } else {
          listingBadges.push(
            <AgxBadge
              key="ListingBadgeLaunchDate"
              type="colourfill"
              colour={colour}
              size="md"
              text={text}
            />
          );
        }
      } else {
        listingBadges.push(
          <AgxBadge
            key="ListingBadgeLaunchDateNotSet"
            type="colourfill"
            colour="lightgrey"
            size="md"
            text={
              launchDateDetails.status === LaunchDetailsStatus.OffMarket
                ? 'Off Market'
                : 'Launch date not set'
            }
          />
        );
      }
    }

    const rexListingBadge = (
      <AgxBadge
        type="colourfill"
        size="md"
        colour="lightgrey"
        text={rexListing ? 'REX ' + rexListing.listingId : 'REX disconnected'}
        leftIcon={
          rexListing ? <AgxCircleIcon color="#54CF09" /> : <AgxCircleIcon color="#DFDFDF" />
        }
        link={rexListingLink}
        rightIcon={<AgxDiagonalUpArrowIcon color="#000" />}
      />
    );

    listingBadges.push(
      isPendingRexListing ? (
        <span key="ListingBadgeRexLink" className="campaignHeaderHiddenBadge" aria-hidden="true">
          {rexListingBadge}
        </span>
      ) : (
        <span key="ListingBadgeRexLink">{rexListingBadge}</span>
      )
    );

    return (
      <AgxSpacer direction="horizontal" gap="spacing-md" fill>
        {listingBadges}
      </AgxSpacer>
    );
  };

  return (
    <>
      {selectedContract ? (
        <ContractSectionHeader contract={selectedContract} />
      ) : (
        <section>
          <AgxSpacer direction="horizontal" fill className="contractCard">
            <CampaignImage selectedCampaign={selectedCampaign} />
            <AgxSpacer fill gap={'sm'}>
              <AgxSpacer direction="vertical" fill gap="none">
                <AgxTypography
                  id="title"
                  type="heading"
                  size="2x-large"
                  color="var(--text-primary)"
                >
                  {addressLineOne ? (
                    <>
                      {addressLineOne}, {addressLineTwo}
                    </>
                  ) : (
                    <>Address Unidentified</>
                  )}
                </AgxTypography>
              </AgxSpacer>
              {generateListingAgents()}
              {generateListingBadges()}
            </AgxSpacer>
          </AgxSpacer>
        </section>
      )}
      {/* The tab bar is a direct child of the scrolling content area so a plain
          CSS `position: sticky` keeps it pinned for the whole page scroll. */}
      <nav className="topMenuContainer">
        <ul className="secondaryMenuContainer horizontal">
          {renderTopMenu(
            SELECTED_CAMPAIGN_TOP_MENU,
            location.state?.activeTab ?? activeTopMenuFromPath ?? MenuTitles.CAMPAIGN
          )}
        </ul>
      </nav>
    </>
  );
};

export default CampaignHeader;
